import type { AgentResponseData, ToolName } from './Agent';
import type { SummaryTemplate } from './Chat';
import { DefaultMinutes, type Minutes } from './Minutes';
import type { RaptorResponse } from './Raptor';

import type { Transcript } from './Transcript';
import type { UserAuth } from './User';

interface _BaseMessage
  extends Record<string, string | boolean | undefined | Date | unknown | number> {
  id: string;
  sender: 'user' | 'assistant' | 'system';
  transcriptId?: string | null;
  conversationId: string;
  pushedAt?: boolean | undefined;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date | undefined;
  model?: string | undefined;
  contentLength?: number | undefined;
}

export type SupportedModels =
  | 'gpt-4o'
  | 'gpt-4o-mini'
  | 'gpt-5.2'
  | 'gpt-5.2-mini'
  | 'gpt-5.2-nano'
  | 'gpt-5'
  | 'gpt-5-mini'
  | 'gpt-5-nano'
  | 'gpt-4.1-nano'
  | 'gpt-4.1-mini'
  | 'gpt-4.1'
  | 'claude-3-opus-latest'
  | 'claude-3-5-sonnet-latest'
  | 'claude-sonnet-4-20250514'
  | 'claude-sonnet-4-5-20250929'
  | 'claude-haiku-4-5-20251001'
  | 'gemini-1.5-pro'
  | 'gemini-1.5-flash'
  | 'gemini-2.5-flash'
  | 'gemini-2.5-pro'
  | 'gemini-3-flash-preview'
  | 'gemini-3-pro-preview'
  | 'openai/gpt-oss-120b'
  | 'openai/gpt-oss-20b'
  | 'meta-llama/llama-4-scout-17b-16e-instruct';

export type SupportedModelExpanded =
  | SupportedModels
  | 'gpt-4.1'
  | 'gpt-4.1-mini'
  | 'gpt-4.1-nano'
  | 'gpt-3.5-turbo'
  | 'gpt-4'
  | 'gpt-4-turbo'
  | 'gpt-4-32k'
  | 'gpt-4-turbo-preview'
  | 'gpt-4-0125-preview'
  | 'gpt-4-turbo-2024-04-09'
  | 'gpt-4o-2024-05-13'
  | 'gpt-4o-mini-2024-07-18';

export interface MessageRow
  extends Record<string, string | boolean | undefined | null | Date | unknown> {
  id: string;
  sender: 'user' | 'assistant' | 'system';
  transcriptId?: string | null;
  conversationId?: string | null;
  pushedAt?: boolean | undefined;
  type: ExchangeMessage['type'];
  subtype?: string | null;
  date: string | undefined;
  sources: string | object | null | undefined; // JSON string of ExchangeSource[] or parsed object
  data: string | object | null | undefined; // JSON string of ResponseData or parsed object
  query: string | null | undefined; // raw query text
  queryOptions: string | object | null | undefined; // JSON string of QueryMessage['queryOptions']
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null | undefined;
}

export interface BaseMessage extends _BaseMessage {
  date: Date;
}

interface SystemMessageBase extends BaseMessage {
  sender: 'system';
}

interface BotMessageBase extends BaseMessage {
  sender: 'assistant';
}
interface UserMessageBase extends BaseMessage {
  sender: 'user';
  shouldChunk?: boolean;
  model?: string;
}

export type SourceType = 'transcript' | 'email' | 'summary';

export interface ExchangeSource extends Record<string, string | undefined | Date> {
  url: string;
  type: SourceType;
  excerpt?: string;
  subtype?: undefined;
  date?: Date;
}

export interface ExchangeSourceTranscript extends ExchangeSource {
  id: string;
  type: 'transcript';
}

export interface ExchangeSourceEmail extends ExchangeSource {
  type: 'email';
  subject?: string;
}

export interface ExchangeSourceSummary extends ExchangeSource {
  type: 'summary';
}

export interface ContextMessage extends UserMessageBase {
  type: 'context';
  subtype: 'minutes' | 'processTranscript' | 'userBackground';
  sources: ExchangeSource[];
}

export interface ProcessTranscriptMessage extends ContextMessage {
  subtype: 'processTranscript';
  shouldChunk: true;
  model: SupportedModelExpanded;
}

export interface MinutesContextMessage extends ContextMessage {
  subtype: 'minutes';
  shouldChunk: true;
  model: SupportedModelExpanded;
}

export interface UserBackgroundContextMessage extends ContextMessage {
  subtype: 'userBackground';
}

export interface SystemRoleMessage extends SystemMessageBase {
  type: 'system';
  subtype: 'processTranscript' | 'answerQuery' | 'minutes' | 'test' | 'agentQuery' | 'businessPlan';
}

export interface MinutesRoleMessage extends SystemRoleMessage {
  subtype: 'minutes';
}

export interface ProcessRoleMessage extends SystemRoleMessage {
  subtype: 'processTranscript';
}

export interface AnswerQueryRoleMessage extends SystemRoleMessage {
  subtype: 'answerQuery';
}

export interface AgentQueryRoleMessage extends SystemRoleMessage {
  subtype: 'agentQuery';
}

export interface TestRoleMessage extends SystemRoleMessage {
  subtype: 'test';
}

export interface BusinessPlanRoleMessage extends SystemRoleMessage {
  subtype: 'businessPlan';
}

export interface QueryFile {
  url: string; // Canonical gs:// URL for storage
  filename: string;
  type: string; // MIME type
  signedUrl?: string; // Temporary signed URL for immediate access
  content?: string; // Extracted text content for documents (included in message)
}

// QueryData - similar to ResponseData but for query messages
export interface QueryData {
  type: 'query';
  _type: 'QueryData';
  files?: QueryFile[];
}

export const isQueryMessage = (message: ExchangeMessage): message is QueryMessage => {
  return (
    message &&
    message.sender === 'user' &&
    (message.type === 'query' ||
      message.type === 'flatQuery' ||
      message.type === 'raptorQuery' ||
      message.type === 'agentQuery')
  );
};

export const isRevisionCheckpointMessage = (
  message: ExchangeMessage
): message is RevisionCheckpointMessage => {
  return message && message.sender === 'assistant' && message.type === 'revisionCheckpoint';
};

export interface _QueryMessage extends UserMessageBase {
  subtype?: undefined;
  query: string;
  queryOptions?: {
    taskIds?: string[];
    includeWorkspaces?: boolean;
    [key: string]: any; // Allow for future extensibility
  };
  data?: QueryData; // Structured data field for files and other query metadata
}

export interface BasicQueryMessage extends _QueryMessage {
  type: 'query';
}

export interface FlatQueryMessage extends _QueryMessage {
  type: 'flatQuery';
}

export interface RaptorQueryMessage extends _QueryMessage {
  type: 'raptorQuery';
}

export interface AgentQueryMessage extends _QueryMessage {
  type: 'agentQuery';
  // taskIds and includeWorkspaces are now part of queryOptions
}

export type QueryMessage =
  | BasicQueryMessage
  | FlatQueryMessage
  | RaptorQueryMessage
  | AgentQueryMessage;

// ResponseData
export interface ResponseData
  extends Record<string, string | Date | string[] | null | RaptorResponse | ToolUse[]> {
  type:
  | 'minutes'
  | 'test'
  | 'basic'
  | 'reworded'
  | 'flat'
  | 'raptor'
  | 'agent'
  | 'businessPlan'
  | 'toolUse'
  | 'revisionCheckpoint';
  fallbackField: 'summary' | 'response' | 'proposed' | 'reasoning' | 'transcription' | 'title';
  // This is a type guard to ensure that the data is a ResponseData.
  model: SupportedModelExpanded | null;
  _type: 'ResponseData';
}

// Extend for Raptor support
export interface RaptorResponseData extends ResponseData {
  type: 'raptor';
  _type: 'ResponseData';
  response: RaptorResponse;
  fallbackField: 'response';
  [key: string]: string | null | RaptorResponse;
}

export interface MinutesResponseData extends Minutes, ResponseData {
  type: 'minutes';
}

export interface FlatResponseData extends ResponseData {
  type: 'flat';
  _type: 'ResponseData';
  fallbackField: 'response';
  [key: string]: string | null | 'basic' | 'flat';
}

export function DefaultFlat(): FlatResponseData {
  return {
    type: 'flat',
    model: null,
    _type: 'ResponseData',
    fallbackField: 'response'
  };
}

export interface RewordedResponseData extends ResponseData {
  type: 'reworded';
  transcription: string;
  fallbackField: 'transcription';
}

export interface QueryResponseData extends ResponseData {
  type: 'basic' | 'flat';
  fallbackField: 'response' | 'proposed' | 'reasoning';
  [key: string]: string | null | 'basic' | 'flat';
}

export interface FlatResponseData extends QueryResponseData {
  type: 'flat';
}
export interface BasicResponseData extends QueryResponseData {
  type: 'basic';
  response: string;
  proposed: string;
  reasoning: string;
}

export interface TestResponseData extends ResponseData {
  summary: string;
  type: 'test';
}

export interface BusinessPlanResponseData extends ResponseData {
  type: 'businessPlan';
  response: string;
  reasoning: string;
  question: string;
  fallbackField: 'response';
  _type: 'ResponseData';
}

export interface RAGSourceNode {
  id: string;
  text: string;
  score: number;
  metadata: Record<string, any>;
}

export interface ToolUseResultItemMetadata extends Record<string, any> {
  sources?: RAGSourceNode[];
  sourceCount?: number;
}

export interface ToolUseResultItem {
  itemType:
  | 'summary'
  | 'transcript'
  | 'email'
  | 'task'
  | 'workspace'
  | 'event'
  | 'thread'
  | 'goal'
  | 'search'
  | 'helpArticle';
  itemId: string;
  title?: string;
  excerpt?: string;
  content?: string;
  score?: number;
  metadata?: ToolUseResultItemMetadata;
}

export interface ToolUse {
  tool: ToolName;
  toolCallId: string;
  functionName: string;
  input: Record<string, any>;
  output: any;
  resultItems: ToolUseResultItem[];
  timestamp: string;
  status: 'pending' | 'in-progress' | 'completed' | 'error';
  error?: string;
  errorMessage?: string;
}

export interface ToolUseData extends ResponseData {
  type: 'toolUse';
  _type: 'ResponseData';
  fallbackField: 'response';
  toolUses: ToolUse[];
  [key: string]: string | null | ToolUse[];
}

// Revision Checkpoint for draft versioning
export interface RevisionCheckpointData extends ResponseData {
  type: 'revisionCheckpoint';
  _type: 'ResponseData';
  fallbackField: 'title';
  draftId: string; // The asset ID of the draft
  draftType: string; // 'email', 'proposal', etc.
  version: string; // Version number as string (e.g., "1", "2", "3")
  title: string; // Draft title or email subject
  summary: string | null; // Brief summary of changes
  lexicalState: string; // Snapshot of the editor state (JSON stringified)
  content: string; // Markdown version of the content
  emailHeaders: string | null; // For email drafts (JSON stringified)
  previousCheckpointId: string | null; // Link to previous checkpoint for undo
}

// ResponseMessage types
// Response to user query
export interface _ResponseMessage extends BotMessageBase {
  type:
  | 'basicResponse'
  | 'minutesResponse'
  | 'testResponse'
  | 'rewordedResponse'
  | 'flatResponse'
  | 'raptorResponse'
  | 'agentResponse'
  | 'businessPlanResponse'
  | 'toolUse'
  | 'revisionCheckpoint';
  subtype?: undefined;
  sources: ExchangeSource[];
  data: ResponseData;
}

export interface QueryResponseMessage extends _ResponseMessage {
  type: 'basicResponse';
  data: QueryResponseData;
}
export interface MinutesResponseMessage extends _ResponseMessage {
  type: 'minutesResponse';
  data: MinutesResponseData;
}

export interface TestResponseMessage extends _ResponseMessage {
  type: 'testResponse';
  data: TestResponseData;
}

export interface RewordedResponseMessage extends _ResponseMessage {
  type: 'rewordedResponse';
  data: RewordedResponseData;
}
export interface FlatResponseMessage extends _ResponseMessage {
  type: 'flatResponse';
  data: FlatResponseData;
}

export interface RaptorResponseMessage extends _ResponseMessage {
  type: 'raptorResponse';
  data: RaptorResponseData;
}

export interface AgentResponseMessage extends _ResponseMessage {
  type: 'agentResponse';
  data: AgentResponseData;
}

export interface BusinessPlanResponseMessage extends _ResponseMessage {
  type: 'businessPlanResponse';
  data: BusinessPlanResponseData;
}

export interface ToolUseMessage extends _ResponseMessage {
  type: 'toolUse';
  data: ToolUseData;
}

export interface RevisionCheckpointMessage extends _ResponseMessage {
  type: 'revisionCheckpoint';
  data: RevisionCheckpointData;
}

export type ResponseMessage =
  | QueryResponseMessage
  | MinutesResponseMessage
  | FlatResponseMessage
  | TestResponseMessage
  | RewordedResponseMessage
  | RaptorResponseMessage
  | AgentResponseMessage
  | BusinessPlanResponseMessage
  | ToolUseMessage
  | RevisionCheckpointMessage;

export type UserExchange = QueryMessage | ContextMessage | ProcessTranscriptMessage;
export type BotExchange =
  | ResponseMessage
  | MinutesResponseMessage
  | FlatResponseMessage
  | QueryResponseMessage
  | TestResponseMessage
  | RewordedResponseMessage
  | RaptorResponseMessage
  | AgentResponseMessage
  | BusinessPlanResponseMessage
  | ToolUseMessage
  | RevisionCheckpointMessage;
export type ExchangeMessage = UserExchange | BotExchange | SystemRoleMessage | ProcessRoleMessage;

export interface HydraterTemplateResponse {
  instructions?: string;
  promptMetadata?: PromptMetadata;
  context?: string;
  attendees?: string;
}

export interface PromptMetadata {
  contentType: string;
  format: string;
  fields?: string[];
  level?: number;
}

export interface Hydrater {
  getSource(source: ExchangeSource): Transcript | undefined;
  getAttendees(transcriptId: string): string[];
  getTemplate(
    messageType: ExchangeMessage['type'],
    messageSubtype: ExchangeMessage['subtype'],
    template: SummaryTemplate,
    sources: ExchangeSource[],
    message?: ExchangeMessage
  ): HydraterTemplateResponse;
  clearCache(): void;
  getTokens(): Pick<UserAuth, 'idToken' | 'refreshToken'> | undefined;
  getTasksForClassification(taskIds?: string[], limit?: number): string;
  getWorkspaceContext(): string;
  setAgentContext?(agentContext: string | null): void;
  getAgentContext?(): string | null;
  getSignedUrl?(canonicalUrl: string): string;
  preCacheFileUrls?(messages: ExchangeMessage[]): Promise<void>;
}

export const ResponseDataDefaults: Record<ResponseData['type'], ResponseData> = {
  minutes: {
    ...DefaultMinutes(),
    type: 'minutes',
    _type: 'ResponseData',
    model: null,
    fallbackField: 'summary'
  },
  flat: {
    type: 'flat',
    _type: 'ResponseData',
    model: null,
    fallbackField: 'response'
  },
  test: {
    type: 'test',
    summary: '',
    _type: 'ResponseData',
    model: null,
    fallbackField: 'summary'
  },
  agent: {
    type: 'agent',
    _type: 'ResponseData',
    response: '',
    reasoning: '',
    question: '',
    model: null,
    fallbackField: 'response'
  },
  basic: {
    type: 'basic',
    response: '',
    proposed: '',
    reasoning: '',
    _type: 'ResponseData',
    model: null,
    fallbackField: 'response'
  },
  reworded: {
    type: 'reworded',
    transcription: '',
    _type: 'ResponseData',
    model: null,
    fallbackField: 'transcription'
  },
  businessPlan: {
    type: 'businessPlan',
    response: '',
    reasoning: '',
    question: '',
    _type: 'ResponseData',
    model: null,
    fallbackField: 'response'
  },
  raptor: {
    type: 'raptor',
    _type: 'ResponseData',
    response: {
      response: '',
      error: null,
      source_nodes: [],
      metadata: {
        response_mode: 'compact',
        mode: 'extract'
      }
    },
    model: null,
    fallbackField: 'response'
  },
  toolUse: {
    type: 'toolUse',
    _type: 'ResponseData',
    model: null,
    fallbackField: 'response',
    toolUses: []
  },
  revisionCheckpoint: {
    type: 'revisionCheckpoint',
    _type: 'ResponseData',
    model: null,
    fallbackField: 'title',
    draftId: '',
    draftType: 'email',
    version: '0',
    title: '',
    content: '',
    lexicalState: JSON.stringify({
      root: { children: [], direction: null, format: '', indent: 0, type: 'root', version: 1 }
    }),
    summary: null,
    emailHeaders: null,
    previousCheckpointId: null
  }
};

export type TemplateMap = Record<ExchangeMessage['type'], SummaryTemplate>;
